// Mirror of useFavoriteVenues for live events. Maintains a Set of favorited
// event IDs, hydrated from the backend on mount and mutated optimistically.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = 'allureEventFavorites';
const FAVORITES_EVENT = 'allure:event-favorites-changed';

const readCache = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeCache = (ids) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore
  }
};

const dispatchChange = (ids) => {
  try {
    window.dispatchEvent(
      new CustomEvent(FAVORITES_EVENT, { detail: { ids } }),
    );
  } catch {
    // ignore
  }
};

export function useFavoriteEvents() {
  const [favoriteIds, setFavoriteIds] = useState(() => new Set(readCache()));
  const inFlightRef = useRef(new Map());

  const token = useMemo(() => {
    try {
      return window.localStorage.getItem('token') || '';
    } catch {
      return '';
    }
  }, []);
  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  useEffect(() => {
    let cancelled = false;
    if (!token) return undefined;
    axios
      .get(`${API}/guest/event-favorites/ids`, { headers })
      .then((res) => {
        if (cancelled) return;
        const ids = Array.isArray(res.data) ? res.data : [];
        const set = new Set(ids);
        setFavoriteIds(set);
        writeCache(Array.from(set));
        dispatchChange(Array.from(set));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [headers, token]);

  useEffect(() => {
    const handler = (event) => {
      const ids = event?.detail?.ids;
      if (!Array.isArray(ids)) return;
      setFavoriteIds(new Set(ids));
    };
    window.addEventListener(FAVORITES_EVENT, handler);
    return () => window.removeEventListener(FAVORITES_EVENT, handler);
  }, []);

  const isFavorite = useCallback(
    (eventId) => favoriteIds.has(eventId),
    [favoriteIds],
  );

  const persist = useCallback(
    async (eventId, nextFavorited) => {
      if (!token) return;
      inFlightRef.current.set(eventId, nextFavorited);
      try {
        if (nextFavorited) {
          await axios.post(
            `${API}/guest/event-favorites/${eventId}`,
            null,
            { headers },
          );
        } else {
          await axios.delete(`${API}/guest/event-favorites/${eventId}`, {
            headers,
          });
        }
        const desired = inFlightRef.current.get(eventId);
        if (desired !== undefined && desired !== nextFavorited) {
          await persist(eventId, desired);
        } else {
          inFlightRef.current.delete(eventId);
        }
      } catch {
        try {
          const res = await axios.get(`${API}/guest/event-favorites/ids`, {
            headers,
          });
          const ids = Array.isArray(res.data) ? res.data : [];
          const set = new Set(ids);
          setFavoriteIds(set);
          writeCache(Array.from(set));
          dispatchChange(Array.from(set));
        } catch {
          // offline — keep optimistic value
        }
        inFlightRef.current.delete(eventId);
      }
    },
    [headers, token],
  );

  const setFavorite = useCallback(
    (eventId, nextFavorited) => {
      if (!eventId) return;
      setFavoriteIds((current) => {
        const next = new Set(current);
        if (nextFavorited) next.add(eventId);
        else next.delete(eventId);
        const ids = Array.from(next);
        writeCache(ids);
        dispatchChange(ids);
        return next;
      });
      persist(eventId, nextFavorited);
    },
    [persist],
  );

  const toggleFavorite = useCallback(
    (eventId) => {
      if (!eventId) return;
      setFavorite(eventId, !favoriteIds.has(eventId));
    },
    [favoriteIds, setFavorite],
  );

  return {
    favoriteIds,
    favoriteCount: favoriteIds.size,
    isFavorite,
    toggleFavorite,
    setFavorite,
  };
}
